// Servers page — compact table (one row per server) + the row's ⋯ menu.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import CreatedBy from '../CreatedBy';
import { SvIcon } from './svIcons';

const TONE = { available: 'var(--sv-green)', busy: 'var(--sv-amber)', offline: 'var(--sv-red-soft)' };

export const serverTone = (status) => TONE[status] || TONE.offline;
export const isLanServer = (s) => typeof s.ip === 'string' && s.ip.startsWith('lan.');
/** Address players use: LAN servers show their LAN (public_ip) address; the panel address is internal. */
export const shownAddress = (s) => (s.lan ? `${s.public_ip || (isLanServer(s) ? '—' : s.ip)}:${s.port}` : `${s.ip}:${s.port}`);

function RowMenu({ server, anchor, onClose, actions }) {
  const { t } = useTranslation();
  const isLan = isLanServer(server);
  const lgsm = !!server.linuxgsm_enabled && !!server.has_backup;
  const live = server.match_status === 'live';
  // <html> is CSS-zoomed (--app-zoom, index.css): the button's rect is in screen pixels, but the
  // menu's top/right/bottom are multiplied by the zoom again - convert to zoomed units first.
  const z = parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
  const rect = anchor.getBoundingClientRect();
  const vw = window.innerWidth / z, vh = window.innerHeight / z;
  const top = rect.top / z, bottom = rect.bottom / z, right = rect.right / z;
  const spaceBelow = vh - bottom;
  const style = { right: Math.max(8, vw - right) };
  if (spaceBelow < 380 && top > spaceBelow) style.bottom = vh - top + 4; else style.top = bottom + 4;

  useEffect(() => {
    const close = (e) => { if (!e.target.closest || (!e.target.closest('.sv-menu') && e.target !== anchor && !anchor.contains(e.target))) onClose(); };
    const closeNow = () => onClose();
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', closeNow, true);
    window.addEventListener('resize', closeNow);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', closeNow, true);
      window.removeEventListener('resize', closeNow);
      document.removeEventListener('keydown', esc);
    };
  }, [anchor, onClose]);

  const item = (icon, label, onClick, opts = {}) => (
    <button type="button" className={opts.tone || ''} disabled={opts.disabled} title={opts.title || ''}
      onClick={() => { onClose(); onClick(); }}>
      <SvIcon name={icon} size={14} />{label}
    </button>
  );

  // Portal to <body>: the page wrapper has a transform (page transition), which would make
  // position: fixed relative to the wrapper instead of the viewport and push the menu down.
  return createPortal(
    <div className="sv-menu" style={style} role="menu">
      {item('zap', t('servers.testRcon', 'Test RCON'), () => actions.testRcon(server))}
      {!isLan && item('link', t('servers.testFiles', 'Test SFTP/FTP'), () => actions.testBackup(server))}
      {item('pin', t('servers.setLocation', 'Set location'), () => actions.setLocation(server))}
      {server.has_backup && (<>
        <hr />
        {item('upload', t('servers.uploadConfig', 'Upload configs'), () => actions.uploadConfig(server), {
          disabled: actions.uploadingConfig === server.id || live,
          title: live ? t('servers.uploadConfigLive', 'Not available while a match is live') : t('servers.uploadConfigHint', 'Upload warmup.cfg, knife.cfg and live.cfg'),
        })}
        {item('folder', t('files.title', 'File Manager'), () => actions.files(server))}
      </>)}
      {lgsm && (<>
        <hr />
        <div className="sv-menu-label">LinuxGSM</div>
        {item('play', t('servers.lgsmStart', 'Start server'), () => actions.lgsm(server, 'start'))}
        {item('restart', t('servers.lgsmRestart', 'Restart server'), () => actions.lgsm(server, 'restart'), { tone: 'warn' })}
        {item('stop', t('servers.lgsmStop', 'Stop server'), () => actions.lgsm(server, 'stop'), { tone: 'danger' })}
        {item('download', t('servers.lgsmUpdate', 'Update server'), () => actions.lgsm(server, 'update'))}
        {item('download', t('servers.lgsmForceUpdate', 'Force update'), () => actions.lgsm(server, 'force-update'), { title: t('servers.lgsmForceUpdateHint', 'Update through SteamCMD without checking for a new build first') })}
        {item('check', t('servers.lgsmValidate', 'Validate files'), () => actions.lgsm(server, 'validate'), { title: t('servers.lgsmValidateHint', 'Check and repair the game files with SteamCMD') })}
        {item('eye', t('servers.lgsmMonitor', 'Monitor'), () => actions.lgsm(server, 'monitor'), { title: t('servers.lgsmMonitorHint', 'Check the server is up and answering; restarts it if it is down') })}
        {item('search', t('servers.lgsmDetails', 'Details'), () => actions.lgsm(server, 'details'), { title: t('servers.lgsmDetailsHint', 'Ports, config files, passwords and status') })}
        {item('drive', t('servers.lgsmBackup', 'Backup'), () => actions.lgsm(server, 'backup'), { title: t('servers.lgsmBackupHint', 'Full archive of the server files (LinuxGSM backup)') })}
        {item('wrench', t('servers.lgsmUpdateLgsm', 'Update LinuxGSM'), () => actions.lgsm(server, 'update-lgsm'), { title: t('servers.lgsmUpdateLgsmHint', 'Update the LinuxGSM scripts themselves') })}
      </>)}
      {server.status === 'busy' && !live && (<>
        <hr />
        {item('unlock', t('servers.forceFree', 'Force Free'), () => actions.forceFree(server), { tone: 'warn', title: t('servers.forceFreeHint', 'Un-stick a server stuck busy after a cancelled/deleted match') })}
      </>)}
      <hr />
      {item('trash', t('common.delete', 'Delete'), () => actions.remove(server), { tone: 'danger' })}
    </div>,
    document.body
  );
}

function ServerRow({ server, selected, onToggle, actions, navigate }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const tone = serverTone(server.status);
  const online = server.status === 'available' || server.status === 'busy';
  const addr = shownAddress(server);
  const players = server.player_count || 0;
  const maxPlayers = server.max_players || 0;
  const statusLabel = {
    available: t('servers.statusAvailable', 'Available'),
    busy: t('servers.statusBusy', 'Busy'),
    offline: t('servers.statusOffline', 'Offline'),
  }[server.status] || t('servers.statusOffline', 'Offline');

  const copy = () => {
    navigator.clipboard.writeText(addr).catch(() => { });
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={`sv-grid sv-row${selected ? ' selected' : ''}`}>
      <div className="sv-col-chk">
        <input type="checkbox" className="sv-check" checked={selected} onChange={() => onToggle(server.id)} aria-label={t('servers.selectServer', 'Select server')} />
      </div>

      <div className="sv-col-name sv-name-cell">
        <div className="sv-name-line">
          <span className="sv-name" title={server.name}>{server.name}</span>
        </div>
        {(server.lan || server.created_by_steam || server.created_by_name) && (
          <div className="sv-sub">
            {server.lan && (
              <button type="button" className="sv-lan" onClick={() => server.lan.event_id && navigate(`/admin/servers/lan/${server.lan.event_id}`)}
                title={server.lan.agent_online ? t('lan.agentOnline', 'Agent online') : t('lan.agentOffline', 'Agent offline')}>
                {t('lan.badge', 'LAN')}: {server.lan.event} / {server.lan.machine}{server.lan.mode === 'tunnel' ? ` (${t('lan.modeTunnelShort', 'Full access')})` : ''}
                {!server.lan.agent_online && <span className="off">{t('lan.agentOffline', 'Agent offline')}</span>}
              </button>
            )}
            <CreatedBy steamId={server.created_by_steam} name={server.created_by_name} />
          </div>
        )}
      </div>

      <div className="sv-col-status sv-status" style={{ '--sv-tone': tone }}>{statusLabel}</div>

      <div className="sv-col-addr sv-addr">
        <code title={addr}>{addr}</code>
        <button type="button" className={`sv-icon-btn sv-copy${copied ? ' done' : ''}`} onClick={copy} title={t('servers.copyAddress', 'Copy address')}>
          <SvIcon name={copied ? 'check' : 'copy'} size={13} />
        </button>
      </div>

      <div className="sv-col-loc sv-loc">
        {server.country_code ? (<>
          <img src={`https://flagcdn.com/20x15/${server.country_code.toLowerCase()}.png`} alt={server.country_code} onError={e => { e.target.style.display = 'none'; }} />
          <span title={server.city ? `${server.city}, ${server.country_name}` : server.country_name}>{server.city || server.country_name}</span>
          {!!server.location_manual && <i className="sv-loc-tag" title={t('servers.locationManualHint', 'Set by hand')} aria-label={t('servers.locationManualHint', 'Set by hand')}><SvIcon name="pin" size={11} /></i>}
        </>) : (
          <button type="button" className="sv-btn small" onClick={() => actions.setLocation(server)}>{t('servers.setLocation', 'Set location')}</button>
        )}
      </div>

      <div className="sv-col-players">
        {online ? (
          <div className="sv-players" title={t('servers.playersOnline', '{{count}} players online', { count: players })}>
            <b>{players}{maxPlayers ? <small>/{maxPlayers}</small> : null}</b>
            {maxPlayers > 0 && <div className="sv-bar"><i style={{ width: `${Math.min(100, (players / maxPlayers) * 100)}%` }} /></div>}
          </div>
        ) : <span className="sv-muted">—</span>}
      </div>

      <div className="sv-col-match" style={{ minWidth: 0 }}>
        {server.match_id ? (
          <button type="button" className="sv-match" onClick={() => navigate(`/admin/matches/${server.match_id}`)} title={t('servers.openMatch', 'Open match')}>
            {server.match_status === 'live' && <span className="live">{t('matchStatus.live', 'Live')}</span>}
            <span className="t1">{server.team1_name}</span>
            <span>vs</span>
            <span className="t2">{server.team2_name}</span>
            <span className="id">#{server.match_id}</span>
          </button>
        ) : <span className="sv-muted">{t('servers.noMatch', 'No match')}</span>}
      </div>

      <div className="sv-col-act sv-actions">
        <button type="button" className="sv-icon-btn" onClick={() => actions.console(server)} title={t('servers.openConsole', 'Console (RCON)')}>
          <SvIcon name="terminal" />
        </button>
        <button type="button" className="sv-icon-btn" onClick={() => actions.files(server)} disabled={!server.has_backup}
          title={server.has_backup ? t('files.title', 'File Manager') : t('servers.filesNeedSftp', 'File Manager needs SFTP/FTP - set it in Edit server')}>
          <SvIcon name="folder" />
        </button>
        <button type="button" className="sv-icon-btn" onClick={() => actions.edit(server)} title={t('servers.editServer', 'Edit server')}>
          <SvIcon name="edit" />
        </button>
        <button type="button" className={`sv-icon-btn${menuAnchor ? ' on' : ''}`} title={t('servers.moreActions', 'More actions')}
          onClick={(e) => { const el = e.currentTarget; setMenuAnchor(a => (a ? null : el)); }}>
          <SvIcon name="more" />
        </button>
        {menuAnchor && <RowMenu server={server} anchor={menuAnchor} onClose={() => setMenuAnchor(null)} actions={actions} />}
      </div>
    </div>
  );
}

export default function ServerTable({ servers, loading, selectedIds, onToggle, onToggleAll, actions, navigate, emptyText, onAdd }) {
  const { t } = useTranslation();
  const selCount = servers.filter(s => selectedIds.has(s.id)).length;
  const all = servers.length > 0 && selCount === servers.length;

  return (
    <div className="sv-table">
      <div className="sv-grid sv-head-row">
        <div>
          <input type="checkbox" className="sv-check" checked={all} ref={el => { if (el) el.indeterminate = selCount > 0 && !all; }}
            onChange={onToggleAll} aria-label={t('common.selectAll', 'Select all')} disabled={!servers.length} />
        </div>
        <div>{t('servers.colServer', 'Server')}</div>
        <div>{t('servers.colStatus', 'Status')}</div>
        <div>{t('servers.colAddress', 'Address')}</div>
        <div className="sv-col-loc">{t('servers.colLocation', 'Location')}</div>
        <div>{t('servers.colPlayers', 'Players')}</div>
        <div>{t('servers.colMatch', 'Match')}</div>
        <div style={{ textAlign: 'right' }}>{t('servers.colActions', 'Actions')}</div>
      </div>

      {loading ? (
        [0, 1, 2, 3].map(i => (
          <div key={i} className="sv-grid sv-row">
            <div /><div className="sv-skeleton" style={{ width: '70%' }} /><div className="sv-skeleton" style={{ width: '60%' }} /><div className="sv-skeleton" style={{ width: '80%' }} />
            <div className="sv-col-loc sv-skeleton" style={{ width: '60%' }} /><div className="sv-skeleton" style={{ width: '50%' }} />
            <div className="sv-skeleton" style={{ width: '75%' }} /><div />
          </div>
        ))
      ) : servers.length === 0 ? (
        <div className="sv-empty">
          <span>{emptyText}</span>
          {onAdd && <button type="button" className="sv-btn primary" onClick={onAdd}><SvIcon name="plus" size={14} />{t('servers.addServer', 'Add Server')}</button>}
        </div>
      ) : (
        servers.map(s => (
          <ServerRow key={s.id} server={s} selected={selectedIds.has(s.id)} onToggle={onToggle} actions={actions} navigate={navigate} />
        ))
      )}
    </div>
  );
}
